import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import createTrend from 'trendline';
import { ExamReportChartType } from '../../api/exam-report';

export type ProgressChartPropsType = {
  examReports: ExamReportChartType[];
};

function ProgressChart({ examReports }: ProgressChartPropsType) {
  const chartData = examReports.map((studentExam, index) => ({
    id: studentExam.id,
    number: index,
    examName: studentExam.exam.name,
    scaledScore: studentExam.scaledScore
  }));

  const length = chartData.length;
  const { slope, data: trendData } = getTrendData(chartData, 'number', 'scaledScore');
  const trendColor = slope === 0 ? BLUE : slope > 0 ? GREEN : RED;
  const trendColorName = slope === 0 ? 'blue' : slope > 0 ? 'green' : 'red';
  const score = (slope * 10).toFixed(0);

  return (
    <>
      <p className='py-4 text-lg font-medium'> نمودار سیر پیشرفت در آزمون‌های دوره‌ای اخیر</p>
      {length === 4 ? (
        <p className={`text-${trendColorName}-500`}>
          <span> امتیاز: </span>
          <span className='ltr inline-block'>{score}</span>
        </p>
      ) : (
        <p className={`text-red-500`}>
          برای کسب امتیاز باید حداقل در 4 آزمون اخیر از آزمون‌های دوره‌ای مدمشاور را شرکت کرده
          باشید.
        </p>
      )}
      <div className='flex justify-center'>
        <ResponsiveContainer
          width='100%'
          aspect={16 / 9}
          minWidth={300}
          debounce={1}
          className='max-w-[1000px]'
        >
          <LineChart data={trendData} margin={{ top: 20, right: 20, left: 20, bottom: 40 }}>
            <Line
              type='monotone'
              dataKey='scaledScore'
              stroke={BLUE}
              strokeWidth={2}
              dot={{ r: 1.5, strokeWidth: 3 }}
            />
            <Line dataKey='scaledScoreTrend' stroke={trendColor} strokeDasharray='3 3' />
            <Tooltip
              labelFormatter={(number, data) => {
                if (!data.length) return null;
                const examName = data[0].payload.examName;
                return <span className='font-medium'>{examName ? examName : number}</span>;
              }}
              formatter={(value: number, label: 'scaledScore' | 'scaledScoreTrend') =>
                label === 'scaledScoreTrend'
                  ? [<span className='ltr inline-block'>{slope}</span>, 'شیب خط روند']
                  : [value, _[label]]
              }
            />
            <CartesianGrid stroke={GRAY} />
            <XAxis
              dataKey='examName'
              tickMargin={12}
              label={{ value: 'آزمون', position: 'bottom', dy: 15 }}
              tickFormatter={value => `آزمون ${value.substring(12, value.indexOf('-') - 1)}`}
            />
            <YAxis
              domain={['dataMin-250', 'dataMax+250']}
              tick={{ dx: -35 }}
              label={{ value: 'تراز', position: 'insideLeft', angle: -90 }}
              tickFormatter={value => value.toFixed(0).toString()}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

const _ = { scaledScore: 'تراز', scaledScoreTrend: 'تراز تخمینی' };
const BLUE = '#1b55b0';
const RED = '#ef4444';
const GREEN = '#22c55e';
const GRAY = '#ccc';

const getTrendData = (data: any[], xKey: string, yKey: string) => {
  const trend = createTrend(data, xKey, yKey);
  return {
    slope: trend.slope,
    data: data.map(d => ({ ...d, [`${yKey}Trend`]: trend.calcY(d[xKey]) }))
  };
};

export default ProgressChart;
